/** Utilidades numéricas puras (sin formato de pantalla). */

export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * Redondea a `decimals` decimales CORRIGIENDO los errores de coma flotante:
 *   roundTo(1.005, 2) → 1.01 (Math.round(1.005 * 100) daría 1)
 *   roundTo(0.1 + 0.2, 2) → 0.3,   roundTo(1e-9, 2) → 0
 * Usa notación exponencial en texto (se redondea el decimal "que se ve", no el binario). Simétrico:
 * los empates se alejan de cero (−1.005 → −1.01). Nunca devuelve −0.
 */
export function roundTo(value: number, decimals = 2): number {
  if (!Number.isFinite(value)) return value;
  const shifted = Math.round(moveDecimal(Math.abs(value), decimals));
  const rounded = moveDecimal(shifted, -decimals);
  return value < 0 && rounded !== 0 ? -rounded : rounded;
}

/** Multiplica por 10^places moviendo el exponente del texto (sin el error binario de `* 10 ** n`). */
function moveDecimal(value: number, places: number): number {
  const [mantissa = '0', exponent = '0'] = String(value).split('e');
  return Number(`${mantissa}e${Number(exponent) + places}`);
}

/** Media aritmética; null si no hay valores. */
export function mean(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/** Mediana; null si no hay valores. */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const upper = sorted[mid] ?? 0;
  return sorted.length % 2 === 1 ? upper : ((sorted[mid - 1] ?? upper) + upper) / 2;
}

/** Variación porcentual de `actual` respecto de `previo`. null si `previo` es 0 o no es finito. */
export function pctChange(actual: number, previo: number): number | null {
  if (!Number.isFinite(actual) || !Number.isFinite(previo) || previo === 0) return null;
  return ((actual - previo) / Math.abs(previo)) * 100;
}

/* ───────────── Aritmética decimal exacta (igual que Decimal en el servidor) ───────────── */

interface Decimal {
  /** valor = m / 10^e */
  m: bigint;
  e: number;
}

/** Decimal exacto de la representación más corta del número (la misma que Python usa en `Decimal(str(x))`). */
export function decimalDe(value: number): Decimal {
  const [mantisa = '0', exponente = '0'] = String(value).split('e');
  const negativo = mantisa.startsWith('-');
  const [entera = '0', fraccion = ''] = mantisa.replace('-', '').split('.');
  const escala = fraccion.length - Number(exponente);
  const digitos = BigInt(entera + fraccion);
  const m = escala < 0 ? digitos * 10n ** BigInt(-escala) : digitos;
  return { m: negativo ? -m : m, e: Math.max(0, escala) };
}

function escalados(a: number, b: number): { A: bigint; B: bigint; e: number } {
  const da = decimalDe(a);
  const db = decimalDe(b);
  const e = Math.max(da.e, db.e);
  return { A: da.m * 10n ** BigInt(e - da.e), B: db.m * 10n ** BigInt(e - db.e), e };
}

/** Divide con redondeo "mitad hacia arriba" alejándose de cero (ROUND_HALF_UP de Decimal). */
function dividirRedondeado(numerador: bigint, denominador: bigint): bigint {
  const negativo = numerador < 0n !== denominador < 0n;
  const n = numerador < 0n ? -numerador : numerador;
  const d = denominador < 0n ? -denominador : denominador;
  const q = (2n * n + d) / (2n * d);
  return negativo ? -q : q;
}

/** (actual − previo) / previo × 100 redondeado a `decimales`, en aritmética exacta. null si previo ≤ 0. */
export function cambioPctExacto(actual: number, previo: number, decimales = 2): number | null {
  if (!Number.isFinite(actual) || !Number.isFinite(previo) || previo <= 0) return null;
  const { A, B } = escalados(actual, previo);
  const q = dividirRedondeado((A - B) * 100n * 10n ** BigInt(decimales), B);
  const valor = Number(q) / 10 ** decimales;
  return valor === 0 ? 0 : valor;
}

/** Clasificación exacta del cambio frente a ±`umbralPct` % (sin pasar por flotantes ni por el redondeo). */
export function clasificarCambioExacto(
  actual: number,
  previo: number,
  umbralPct = 1,
): 'sube' | 'baja' | 'estable' | null {
  if (!Number.isFinite(actual) || !Number.isFinite(previo) || previo <= 0) return null;
  const { A, B } = escalados(actual, previo);
  const u = decimalDe(umbralPct);
  const cambio100 = (A - B) * 100n * 10n ** BigInt(u.e);
  const limite = u.m * B;
  if (cambio100 >= limite) return 'sube';
  return cambio100 <= -limite ? 'baja' : 'estable';
}

/** actual − previo exacto, redondeado a `decimales`. */
export function restaExacta(actual: number, previo: number, decimales = 4): number {
  const { A, B, e } = escalados(actual, previo);
  const q = dividirRedondeado((A - B) * 10n ** BigInt(decimales), 10n ** BigInt(e));
  const valor = Number(q) / 10 ** decimales;
  return valor === 0 ? 0 : valor;
}

/**
 * Producto exacto de los factores dividido por 10^`desplazar` (p. ej. 2 para aplicar un porcentaje), redondeado
 * a `decimales` con "mitad hacia arriba" (alejándose de cero). NaN si falta algún factor o no es finito.
 * Evita que un empate de centavos caiga mal por el error binario: productoExacto([3, 0.335]) → 1.01.
 */
export function productoExacto(factores: readonly number[], decimales = 2, desplazar = 0): number {
  if (factores.length === 0 || factores.some((f) => !Number.isFinite(f))) return Number.NaN;
  let m = 1n;
  let e = desplazar;
  for (const factor of factores) {
    const d = decimalDe(factor);
    m *= d.m;
    e += d.e;
  }
  const q =
    e <= decimales
      ? m * 10n ** BigInt(decimales - e)
      : dividirRedondeado(m, 10n ** BigInt(e - decimales));
  const valor = Number(q) / 10 ** decimales;
  return valor === 0 ? 0 : valor;
}

/** dividendo / divisor exacto, redondeado a `decimales` (mitad hacia arriba). NaN si el divisor es 0 o algo no es finito. */
export function cocienteExacto(dividendo: number, divisor: number, decimales = 2): number {
  if (!Number.isFinite(dividendo) || !Number.isFinite(divisor) || divisor === 0) return Number.NaN;
  const a = decimalDe(dividendo);
  const b = decimalDe(divisor);
  const q = dividirRedondeado(a.m * 10n ** BigInt(b.e + decimales), b.m * 10n ** BigInt(a.e));
  const valor = Number(q) / 10 ** decimales;
  return valor === 0 ? 0 : valor;
}
